// libs
import React, { memo, useRef, useEffect, useMemo, Fragment } from 'react';

//components
import FilterCheckbox from 'shared/components/Filter/components/FilterCheckbox';
import FilterRadio from 'shared/components/Filter/components/FilterRadio';

//styles
import { ListContainer, FallbackText, LetterHeading } from './styles';

// defs
import { ALIGNMENT_TYPE, Value } from 'shared/components/Filter/types';

interface IFiltersList {
  selectedOptions: string[];
  values: Value[];
  onFilterSelection: (item: string) => VoidFunction;
  type: string;
  searchText?: string;
  selectedAlphabet?: string;
  showAlphabetHeading?: boolean;
  isPopUp?: boolean;
}

//constants
const FALLBACK_TEXT = 'Uh Oh. We’re sorry, we couldn’t find any results for';
const SINGLE_SELECT = 'single-select';
const MAX_COLUMN_COUNT = 3;

function FiltersList({
  selectedOptions,
  searchText,
  selectedAlphabet = '',
  values,
  showAlphabetHeading = false,
  onFilterSelection,
  isPopUp = false,
  type,
}: IFiltersList) {
  const valuesListRef = useRef<HTMLDivElement>(null);
  const letterHeadingRef = useRef<Record<string, HTMLElement>>({});
  const FilterComponent = type === SINGLE_SELECT ? FilterRadio : FilterCheckbox;

  useEffect(() => {
    if (isPopUp) {
      if (valuesListRef?.current && letterHeadingRef?.current[selectedAlphabet])
        valuesListRef.current.scrollLeft =
          letterHeadingRef.current[selectedAlphabet].offsetLeft - 20;
    }
  }, [isPopUp, selectedAlphabet]);

  useEffect(() => {
    if (searchText !== '' && valuesListRef?.current) valuesListRef.current.scrollLeft = 0;
  }, [searchText]);

  const columnCount = useMemo(() => {
    if (showAlphabetHeading || values.length === 0) return MAX_COLUMN_COUNT;
    if (values.length <= 8) return 1;
    else if (values.length > 8 && values.length <= 18) return 2;
    return MAX_COLUMN_COUNT;
  }, [showAlphabetHeading, values.length]);

  return (
    <ListContainer isPopUp={isPopUp} ref={valuesListRef} columnCount={columnCount}>
      {values.length ? (
        values.map((filteredValue) => {
          const isValueSelected =
            !showAlphabetHeading && selectedOptions.includes(filteredValue.id);

          const isDisabled =
            selectedAlphabet && filteredValue.letterHeading !== selectedAlphabet ? true : false;

          return (
            <Fragment key={filteredValue.name}>
              {showAlphabetHeading ? (
                <>
                  {filteredValue.letterHeading && (
                    <LetterHeading
                      key={filteredValue.letterHeading}
                      ref={(el) => (letterHeadingRef.current[filteredValue.letterHeading] = el)}
                      isDisabled={isDisabled}
                    >
                      {filteredValue.letterHeading}
                    </LetterHeading>
                  )}
                  {filteredValue.values &&
                    filteredValue.values.map((value) => {
                      let _isValueSelected = selectedOptions.includes(value.id);
                      return (
                        <FilterComponent
                          key={value.id}
                          value={value}
                          alignmentType={isPopUp ? ALIGNMENT_TYPE.LEFT : ALIGNMENT_TYPE.RIGHT}
                          onFilterSelection={onFilterSelection}
                          isSelected={_isValueSelected}
                          isHighlighted={!isDisabled}
                        />
                      );
                    })}
                </>
              ) : (
                <FilterComponent
                  value={filteredValue}
                  alignmentType={isPopUp ? ALIGNMENT_TYPE.LEFT : ALIGNMENT_TYPE.RIGHT}
                  onFilterSelection={onFilterSelection}
                  isSelected={isValueSelected}
                />
              )}
            </Fragment>
          );
        })
      ) : (
        <FallbackText>{`${FALLBACK_TEXT} "${searchText}"`}</FallbackText>
      )}
    </ListContainer>
  );
}

export default memo(FiltersList);
